import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import {
  Button,
  CheckMarkIcon,
  CircularLoader,
  InfoIcon,
  TrashFilledIcon,
  Typography,
} from '@blade/ui-kit'
import { Close4Icon } from '@blade/ui-kit'
import { BoosterSingleColorIcon } from '@blade/ui-kit'

import { PremiumGradientIcon } from '../icons'

import type { Intent } from '../theme'
import type { IconProps, Size } from '@blade/ui-kit'

type IconIntent = Intent | 'premium' | 'booster'

type InfoIconProps = {
  intent?: IconIntent
}

type IconComponent = React.FC<InfoIconProps>

type InfoDrawerBodyComponent = React.FC<InfoDrawerBodyProps> & {
  TrashIcon: IconComponent
  ErrorIcon: IconComponent
  SuccessIcon: IconComponent
  InfoIcon: IconComponent
  PremiumIcon: IconComponent
  BoosterIcon: IconComponent
}

export type InfoDrawerBodyProps = {
  header?: React.ReactNode
  title?: React.ReactNode
  description?: React.ReactNode
  busy?: boolean
  icon?: React.ReactNode
  intent?: Intent
  confirm?: {
    label: React.ReactNode
    onClick: () => void
  }
  cancel?: {
    label: React.ReactNode
    onClick: () => void
  }
  other?: {
    icon: React.ReactElement<IconProps>
    onClick: () => void
  }
}

const ICON_SIZE = 28
const BUTTON_SIZE: Size = 's'

const InfoDrawerBodyRoot: React.FC<InfoDrawerBodyProps> = ({
  icon,
  title,
  description,
  intent,
  busy,
  confirm,
  cancel,
  other,
}) => {
  const { t } = useTranslation()

  const hasActions = cancel || confirm || busy
  const hasIconNotBusy = icon && !busy

  return (
    <>
      {hasIconNotBusy ? icon : undefined}
      {busy ? <BusyIcon /> : undefined}
      <Body>
        {title ? <Subtitle data-intent={intent}>{title}</Subtitle> : undefined}
        {description ? <Description>{description}</Description> : undefined}
      </Body>
      {hasActions ? (
        <Action data-layout={other ? 'other-button' : 'multiple-buttons'}>
          {busy ? (
            <Button
              disabled
              type="button"
              variant="soft"
              radius="rounded"
              size={BUTTON_SIZE}
            >
              {t('common.loading')}...
            </Button>
          ) : undefined}
          {cancel && !busy ? (
            <ButtonCancel
              type="button"
              variant="secondary"
              radius="rounded"
              size={BUTTON_SIZE}
              onClick={cancel.onClick}
            >
              {cancel.label ?? t('common.cancel')}
            </ButtonCancel>
          ) : undefined}
          {confirm && !busy ? (
            <ButtonConfirm
              variant="solid"
              size={BUTTON_SIZE}
              type="button"
              radius="rounded"
              color="primary"
              onClick={confirm.onClick}
              busy={busy}
              data-intent={intent}
            >
              {confirm.label ?? t('common.continue')}
            </ButtonConfirm>
          ) : undefined}
          {other && !busy ? (
            <ButtonOther
              type="button"
              variant="soft"
              radius="rounded"
              size={BUTTON_SIZE}
              onClick={other.onClick}
              iconLeft={other.icon}
            />
          ) : undefined}
        </Action>
      ) : undefined}
    </>
  )
}

const TrashIcon: IconComponent = ({ intent = 'error' }) => (
  <IconWrapper data-intent={intent}>
    <TrashFilledIcon size={ICON_SIZE} />
  </IconWrapper>
)
const ErrorIcon: IconComponent = ({ intent = 'error' }) => (
  <IconWrapper data-intent={intent}>
    <Close4Icon size={ICON_SIZE} />
  </IconWrapper>
)
const SuccessIcon: IconComponent = ({ intent = 'success' }) => (
  <IconWrapper data-intent={intent}>
    <CheckMarkIcon size={ICON_SIZE} />
  </IconWrapper>
)
const InfoDrawerInfoIcon: IconComponent = ({ intent = 'neutral' }) => (
  <IconWrapper data-intent={intent}>
    <InfoIcon size={ICON_SIZE} />
  </IconWrapper>
)
const PremiumIcon: IconComponent = ({ intent = 'premium' }) => (
  <IconWrapper data-intent={intent}>
    <PremiumGradientIcon size={ICON_SIZE} />
  </IconWrapper>
)
const BoosterIcon: IconComponent = ({ intent = 'booster' }) => (
  <IconWrapper data-intent={intent}>
    <BoosterSingleColorIcon size={ICON_SIZE} />
  </IconWrapper>
)

export const InfoDrawerBody: InfoDrawerBodyComponent = Object.assign(
  InfoDrawerBodyRoot,
  {
    TrashIcon,
    ErrorIcon,
    SuccessIcon,
    InfoIcon: InfoDrawerInfoIcon,
    PremiumIcon,
    BoosterIcon,
  },
)

const BusyIcon = () => (
  <IconWrapper>
    <CircularLoader size={ICON_SIZE} />
  </IconWrapper>
)

const Body = styled.div`
  display: grid;

  gap: 8px;
  padding: 0 12px;
  text-align: center;
`

const Subtitle = styled(Typography).attrs({
  variant: 'body1-bold',
})`
  color: var(--text-12);

  &[data-intent='error'] {
    color: var(--error-8);
  }
`

const Description = styled(Typography).attrs({
  variant: 'body2-regular',
})`
  color: var(--text-9);
`

const Action = styled.footer`
  gap: 5px;
  padding: 22px 12px var(--safe-bottom);

  &[data-layout='multiple-buttons'] {
    display: grid;
    grid-auto-flow: column;
  }
  &[data-layout='other-button'] {
    display: grid;
    grid-template-columns: 1fr auto;
  }
`

const ButtonCancel = styled(Button)`
  color: var(--text-11);
`
// confirm button to be red when the intent equals error
const ButtonConfirm = styled(Button)`
  &[data-intent='error'] {
    background: var(--error-8);
    color: var(--text-12);
    &:hover {
      background: var(--error-9);
    }
  }
  &[data-intent='neutral'] {
    background: var(--neutral-4);
    color: var(--text-12);
    &:hover {
      background: var(--neutral-5);
    }
  }
`

const ButtonOther = styled(Button)`
  width: 40px;
  padding: 0;
`

const IconWrapper = styled.div`
  border: 0.5px solid var(--textA-3);

  place-self: center;
  margin-bottom: 24px;

  display: grid;
  width: 60px;
  height: 60px;
  padding: 12px;
  justify-content: center;
  align-items: center;

  border-radius: 16px;

  &[data-intent='error'] {
    border: 0.5px solid var(--error-8);

    svg {
      color: var(--error-8);
    }
  }
  &[data-intent='neutral'] {
    border: 0.5px solid var(--neutral-11);

    svg {
      color: var(--neutral-11);
    }
  }
  &[data-intent='success'] {
    border: 0.5px solid var(--success-8);

    svg {
      color: var(--success-8);
    }
  }
  // TODO: take color from booster-subscription theme colors
  &[data-intent='premium'] {
    border: 0.5px solid var(--primary-9);
  }
  &[data-intent='booster'] {
    border: 0.5px solid var(--acid-12);
    svg {
      color: var(--acid-12);
    }
  }
`
